import app from 'flarum/forum/app';
import { syncServerTime, serverNow, msUntil } from '../../common/clock';
import { playEffect } from '../../common/sound';

/**
 * Owns one contest's data and its polling loop.
 *
 * Polling is driven by the schedule rather than by a fixed tick: the next
 * refresh is aimed at the next question boundary, with a slower catch-up poll
 * in between. Background tabs back off, a returning tab resyncs immediately,
 * and failures retry with growing delays plus jitter so a whole audience does
 * not stampede the server in lockstep at the same boundary.
 */
export default class ContestState {
  constructor(contestId) {
    this.contestId = contestId;

    this.contest = null;
    this.live = null;
    this.results = null;

    this.loading = true;
    this.error = null;

    // Answer submission status for the open question.
    this.submitting = false;
    this.submitFailed = false;
    this.pendingOptionId = null;

    // Joker (50/50) status for the open question.
    this.jokerPending = false;
    this.jokerFailed = false;

    this.timer = null;
    this.failureCount = 0;
    this.destroyed = false;
    this.refreshPromise = null;
    this.refreshSequence = 0;
    this.appliedRefreshSequence = 0;

    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  destroy() {
    this.destroyed = true;
    this.clearTimer();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * A tab coming back to the foreground resyncs at once, so a user who
   * switched away lands on the correct question immediately.
   */
  onVisibilityChange() {
    if (!document.hidden && !this.destroyed) {
      this.refresh();
    }
  }

  get phase() {
    return this.live?.state?.phase || this.contest?.state?.phase || null;
  }

  get question() {
    return this.live?.question || null;
  }

  get isRevealing() {
    return !!this.live?.state?.isRevealing;
  }

  /**
   * Initial load: the full contest payload, then the polling loop.
   */
  load() {
    const requestStartedAt = Date.now();

    return app
      .request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/happy-quiz/contests/${this.contestId}`,
      })
      .then((response) => {
        const responseReceivedAt = Date.now();

        this.contest = response.data;
        this.results = response.data.results || null;
        syncServerTime(this.contest.state?.serverNow, requestStartedAt, responseReceivedAt);

        this.loading = false;
        this.error = null;
        this.failureCount = 0;

        // The overview already carries the current phase; the live payload
        // fills in the open question when there is one.
        this.scheduleNext();
        m.redraw();

        if (this.phase === 'live') {
          this.refresh();
        }
      })
      .catch((error) => {
        this.loading = false;
        this.error = error;
        m.redraw();
      });
  }

  /**
   * Fetches the lightweight state payload.
   */
  refresh() {
    if (this.destroyed) return Promise.resolve();
    if (this.refreshPromise) return this.refreshPromise;

    const requestId = ++this.refreshSequence;
    const requestStartedAt = Date.now();

    const request = app
      .request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/happy-quiz/contests/${this.contestId}/state`,
      })
      .then((response) => {
        const responseReceivedAt = Date.now();

        // A response from an older request must never replace newer state.
        if (this.destroyed || requestId < this.appliedRefreshSequence) return;

        this.appliedRefreshSequence = requestId;

        const previousIndex = this.live?.state?.questionIndex;

        this.live = response.data;
        syncServerTime(this.live.state?.serverNow, requestStartedAt, responseReceivedAt);

        if (response.data.results) {
          this.results = response.data.results;
        }

        // A new question means the previous submission UI no longer applies.
        if (this.live.state?.questionIndex !== previousIndex) {
          this.submitting = false;
          this.submitFailed = false;
          this.pendingOptionId = null;
          this.jokerPending = false;
          this.jokerFailed = false;
        }

        // Keep the overview's phase in step so the promo/lobby/results
        // switch does not lag behind the live payload.
        if (this.contest) {
          this.contest.state = this.live.state;
        }

        this.failureCount = 0;
        this.error = null;

        const nextDelayMs = this.scheduleNext();

        console.info('[HappyQuiz][poll]', {
          result: 'success',
          requestId,
          rttMs: responseReceivedAt - requestStartedAt,
          questionIndex: this.live.state?.questionIndex ?? null,
          phase: this.live.state?.phase ?? null,
          isRevealing: !!this.live.state?.isRevealing,
          serverNow: this.live.state?.serverNow ?? null,
          nextDelayMs,
        });

        m.redraw();
      })
      .catch((error) => {
        if (this.destroyed) return;

        // Transient failures must not freeze the run; back off and retry.
        this.failureCount++;
        const nextDelayMs = this.scheduleNext();
        const apiError = error?.response?.errors?.[0];

        console.warn('[HappyQuiz][poll]', {
          result: 'error',
          requestId,
          rttMs: Date.now() - requestStartedAt,
          questionIndex: this.live?.state?.questionIndex ?? null,
          phase: this.live?.state?.phase ?? this.contest?.state?.phase ?? null,
          httpStatus: error?.status ?? error?.response?.status ?? null,
          errorCode: apiError?.code ?? error?.name ?? 'network_error',
          failureCount: this.failureCount,
          nextDelayMs,
        });
      })
      .finally(() => {
        if (this.refreshPromise === request) {
          this.refreshPromise = null;
        }
      });

    this.refreshPromise = request;

    return request;
  }

  /**
   * Picks when to poll next.
   *
   * Aims at the next phase boundary so a question change is picked up the
   * instant it happens, with a coarser catch-up poll in between.
   */
  scheduleNext() {
    this.clearTimer();

    if (this.destroyed) return null;

    const delay = this.nextDelay();

    this.timer = setTimeout(() => this.refresh(), delay);

    return delay;
  }

  nextDelay() {
    // Exponential backoff after failures, capped, so an outage does not turn
    // into a request flood.
    if (this.failureCount > 0) {
      const backoff = Math.min(60000, 2000 * Math.pow(2, this.failureCount - 1));

      return backoff + this.jitter(backoff * 0.25);
    }

    const state = this.live?.state || this.contest?.state;
    const phase = state?.phase;

    // A hidden tab has nothing to draw; poll rarely and resync on return.
    if (document.hidden) {
      return 60000 + this.jitter(15000);
    }

    if (phase === 'live') {
      const boundary = state.isRevealing ? state.revealEndsAt : state.questionClosesAt;
      const untilBoundary = msUntil(boundary);

      const catchUp = this.configuredSeconds('happyQuizPollLive', 10) * 1000;

      // Land just after the boundary rather than exactly on it, and spread
      // the audience over a short window instead of one simultaneous burst.
      if (untilBoundary > 0 && untilBoundary < catchUp) {
        return untilBoundary + 250 + this.jitter(750);
      }

      // The local estimate can reach the boundary a fraction before the
      // authoritative server does. If that early poll returns the same state,
      // retry promptly instead of falling through to the 10-second catch-up
      // interval and showing the old question for most of the next one.
      if (untilBoundary === 0) {
        return 500 + this.jitter(500);
      }

      return catchUp + this.jitter(catchUp * 0.2);
    }

    if (phase === 'waiting' || phase === 'scheduled') {
      const untilStart = msUntil(state?.startsAt);
      const lobby = this.configuredSeconds('happyQuizPollLobby', 30) * 1000;

      if (untilStart > 0 && untilStart < lobby) {
        return untilStart + 250 + this.jitter(750);
      }

      return lobby + this.jitter(lobby * 0.2);
    }

    if (phase === 'finalizing') {
      // Results are being written; check back quickly but not aggressively.
      return 3000 + this.jitter(1500);
    }

    // Completed or cancelled: nothing more will change.
    return 300000;
  }

  configuredSeconds(attribute, fallback) {
    const value = parseInt(app.forum.attribute(attribute), 10);

    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  /**
   * Random spread so simultaneous clients do not align their requests.
   */
  jitter(range) {
    return Math.floor(Math.random() * Math.max(0, range));
  }

  /**
   * Registers the current user for the contest.
   */
  join() {
    const requestStartedAt = Date.now();

    return app
      .request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/happy-quiz/contests/${this.contestId}/join`,
      })
      .then((response) => {
        const responseReceivedAt = Date.now();

        this.contest = response.data;
        syncServerTime(this.contest.state?.serverNow, requestStartedAt, responseReceivedAt);
        this.scheduleNext();
        m.redraw();
      });
  }

  /**
   * Submits an answer for the open question.
   *
   * The question id is captured at send time, because the response may arrive
   * after the contest has already moved on — in which case the result must not
   * be applied to whatever question is open by then.
   */
  submitAnswer(optionId) {
    const question = this.question;

    if (!question || this.submitting) return Promise.resolve();

    // An answer already accepted for this question is final.
    if (this.live?.myAnswer) return Promise.resolve();

    const questionId = question.id;

    // Stable per question, so a retry of the same choice is recognised as the
    // same request rather than a second answer.
    const idempotencyKey = `${this.contestId}-${questionId}-${optionId}`;

    this.submitting = true;
    this.submitFailed = false;
    this.pendingOptionId = optionId;

    if (this.contest?.soundEffectsEnabled) playEffect('tick');

    m.redraw();

    return app
      .request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/happy-quiz/contests/${this.contestId}/answers`,
        body: { data: { questionId, optionId, idempotencyKey } },
      })
      .then(() => {
        if (this.destroyed) return;

        this.submitting = false;

        // Only reflect the acceptance if we are still on that question.
        if (this.question?.id === questionId) {
          this.live.myAnswer = { selectedOptionId: optionId, isCorrect: null };
        }

        m.redraw();
      })
      .catch((error) => {
        if (this.destroyed) return;

        this.submitting = false;

        // A rejection because the window closed is not a retryable failure.
        const code = error?.response?.errors?.[0]?.code;

        this.submitFailed = code !== 'window_closed';
        this.submitError = code || null;

        m.redraw();
      });
  }

  /**
   * Spends one 50/50 on the open question.
   *
   * Captures the question id at send time for the same reason submitAnswer
   * does: the response must not land on whatever question is open by the time
   * it arrives.
   */
  useJoker() {
    const question = this.question;

    if (!question || this.jokerPending) return Promise.resolve();

    // Already used on this question, or already answered: nothing to do.
    if (this.live?.myJoker || this.live?.myAnswer) return Promise.resolve();

    const questionId = question.id;

    this.jokerPending = true;
    this.jokerFailed = false;
    m.redraw();

    return app
      .request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/happy-quiz/contests/${this.contestId}/joker`,
        body: { data: { questionId } },
      })
      .then((response) => {
        if (this.destroyed) return;

        this.jokerPending = false;

        // Only reflect the result if we are still on that question.
        if (this.question?.id === questionId) {
          this.live.myJoker = { hiddenOptionIds: response.data.hiddenOptionIds };

          if (Number.isFinite(this.live.jokersRemaining)) {
            this.live.jokersRemaining = Math.max(0, this.live.jokersRemaining - 1);
          }
        }

        m.redraw();
      })
      .catch(() => {
        if (this.destroyed) return;

        this.jokerPending = false;
        this.jokerFailed = true;
        m.redraw();
      });
  }
}
